/** MOMAX Home Banner V6 — first-viewport fit and immediate native-scroll reveal. */
(() => {
  if (customElements.get('momax-home-banner')) return;
  customElements.define('momax-home-banner', class extends HTMLElement {
    connectedCallback() {
      if (this.wAbort) return;
      this.initWaitlist();
    }
    disconnectedCallback() {
      if (!this.wAbort) return;
      this.destroyWaitlist();
      this.wAbort = null;
    }
    get isFloat() { return this.classList.contains('momax-home-banner--float'); }
    setupHeaderSlot() {
      if (!this.owner) return;
      this.headerElement = document.querySelector('[data-momax-header]');
      this.headerShell = this.headerElement?.parentElement;
      if (!this.headerShell) return;
      if (!this.isFloat) {
        this.headerShell.removeAttribute('data-home-banner-header-slot');
        this.headerShell.style.removeProperty('--home-header-slot-height');
        return;
      }
      this.measureHeaderSlot();
      this.headerShell.setAttribute('data-home-banner-header-slot', '');
    }
    measureHeaderSlot() {
      if (!this.headerShell || !this.headerElement) return;
      const main = this.headerElement.querySelector('.momax-header__main');
      if (!main) return;
      const height = [...main.children].reduce((sum, child) => sum + child.getBoundingClientRect().height, 0);
      this.headerShell.style.setProperty('--home-header-slot-height', `${height}px`);
      this.style.setProperty('--home-header-slot-height', `${height}px`);
    }
    header(form, animate = false) {
      if (this.dataset.headerForm === form) return;
      this.dataset.headerForm = form;
      if (this.owner) window.MomaxHeader?.setForm(form, { animate });
    }
    initWaitlist() {
      this.wAbort = new AbortController();
      this.wSignal = this.wAbort.signal;
      this.wStage = this.querySelector('.momax-home-banner__stage');
      this.wSlider = this.querySelector('[data-banner-slider]');
      this.wSlides = this.wSlider ? [...this.wSlider.querySelectorAll('[data-banner-slide]')] : [];
      this.wDots = [...this.querySelectorAll('[data-banner-dot]')];
      this.wControls = this.querySelector('.momax-home-banner__bottom-controls');
      this.wPause = this.querySelector('[data-banner-pause]');
      this.wCue = this.querySelector('[data-mm-hero-scroll-cue]');
      this.wMobileCue = this.querySelector('[data-home-mobile-scroll-trigger]');
      this.wReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.wMobile = window.matchMedia('(max-width: 749px)');
      this.wFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.wAutoplay = this.wSlider?.dataset.autoplay === 'true';
      this.wInterval = Math.max(3000, Number(this.wSlider?.dataset.interval) || 5000);
      this.wIndex = 0;
      this.wLatest = 0;
      this.wRevealed = false;
      this.wPaused = false;
      this.wEditorSelected = false;
      this.wFocusPaused = false;
      this.wInView = true;
      this.wVisibleSince = null;
      this.wElapsed = 0;
      this.wTimer = null;
      this.wPreloadTimer = null;
      this.wQueued = false;
      this.wLoads = new Map();
      this.wForegroundLoaded = new Set();
      this.wPointer = null;
      this.wClickSuppress = 0;
      this.wLastWheel = 0;
      this.owner = document.querySelector('momax-home-banner') === this;
      this.setupHeaderSlot(); // Original floating header integration.
      if (this.owner) this.header(this.wMobile.matches ? 'compact' : 'full', false);
      // Establish the exact viewport area beneath the header before the first reveal.
      this.wMeasureFirstScreen();
      this.wListener(this, 'click', e => this.wClick(e), {capture:true});
      if (this.wCue) this.wCue.hidden = false;
      if (this.wControls) this.wControls.dataset.autoplay = String(this.wAutoplay);
      // Initial media request happens at parse-time via the tiny inline promoter.
      this.wLoad(0);
      this.wPaint(0);
      this.wSlides.forEach(slide => {
        slide.querySelectorAll('[data-banner-element]').forEach((part, i) => {
          part.style.setProperty('--home-waitlist-delay', `${Math.min(i * 85, 340)}ms`);
        });
      });
      this.classList.add('is-waitlist-ready');
      this.wRefresh();
      // Shopify/header scripts can finalize their layout after our custom element connects.
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.wMeasureFirstScreen();
        this.wRefresh();
      });
      this.wListener(window, 'scroll', () => this.wSchedule(), {passive:true});
      this.wListener(window, 'resize', () => {
        this.measureHeaderSlot();
        if (this.owner) this.header(this.wMobile.matches || this.wRevealed ? 'compact' : 'full', false);
        this.wMeasureFirstScreen();
        this.wSchedule();
      }, {passive:true});
      this.wListener(document, 'visibilitychange', () => {
        if (document.hidden) this.wStop(); else this.wStart();
      });
      this.wListener(this,'focusin', e => {
        if (!e.target.matches?.(':focus-visible') || e.target === this.wMobileCue || this.wCue?.contains(e.target)) return;
        this.wFocusPaused = true; this.wStop();
      });
      this.wListener(this,'focusout', e => {
        if (!this.contains(e.relatedTarget)) { this.wFocusPaused = false; this.wStart(); }
      });
      this.wListener(this.wReduce,'change', () => {
        if (this.wReduce.matches) this.wStop(); else this.wStart();
      });
      this.wListener(this.wStage,'pointerdown',e => this.wPointerDown(e));
      this.wListener(this.wStage,'pointermove',e => this.wPointerMove(e));
      this.wListener(this.wStage,'pointerup',e => this.wPointerUp(e));
      this.wListener(this.wStage,'pointercancel',() => this.wPointerCancel());
      this.wListener(this.wStage,'dragstart',e => {
        if (e.target.closest?.('.momax-home-banner__picture img')) e.preventDefault();
      });
      // Preserve desktop horizontal-wheel slide navigation; do not put a
      // non-passive wheel listener on mobile/touch-only devices.
      if (this.wFinePointer.matches && !this.wMobile.matches) {
        this.wListener(this.wStage,'wheel', e => this.wWheel(e), {passive:false});
      }
      this.wListener(document, 'shopify:block:select', e => {
        this.wSyncSlides(); // Editor may have replaced the selected slide immediately beforehand.
        const blockId = e.detail?.blockId;
        if (!blockId) return;
        const i = this.wSlides.findIndex(slide => slide.dataset.blockId === blockId || slide.contains(e.target) || [...slide.querySelectorAll('[data-block-id]')].some(part => part.dataset.blockId === blockId));
        if (i < 0) return;
        this.wEditorSelected = true;
        this.wStop(); this.wSetRevealed(true);
        this.wShow(i);
      });
      this.wListener(document, 'shopify:block:deselect', e => {
        if (!this.contains(e.target)) return;
        this.wEditorSelected = false;
        this.wRefresh(); this.wStart();
      });
      this.wListener(document, 'shopify:section:load', e => {
        // The section can be re-rendered in Shopify without reloading the entire page.
        if (e.target !== this.closest('.shopify-section') && !this.contains(e.target)) return;
        this.wSyncSlides();
        this.setupHeaderSlot(); this.wMeasureFirstScreen(); this.wSchedule();
      });
      if (window.Shopify?.designMode && this.wSlider) {
        // The theme editor replaces nested theme blocks in place. Reconcile after
        // childList edits, and keep observers out of the storefront hot path.
        this.wEditorObserver = new MutationObserver(() => {
          if (this.wEditorQueued) return;
          this.wEditorQueued = true;
          requestAnimationFrame(() => {
            this.wEditorQueued = false;
            if (this.isConnected) this.wSyncSlides(true);
          });
        });
        this.wEditorObserver.observe(this.wSlider, {childList: true, subtree: true});
      }
      if ('IntersectionObserver' in window && this.wStage) {
        this.wObserver = new IntersectionObserver(entries => {
          const visible = entries.some(e => e.isIntersecting);
          if (visible === this.wInView) return;
          this.wInView = visible;
          if (visible) { this.wRefresh(); this.wStart(); }
          else this.wStop();
        }, { threshold: 0 });
        this.wObserver.observe(this.wStage);
      }
    }
    wSyncSlides(refreshEditedContent = false) {
      if (!this.wSlider) return;
      const nodes = [...this.wSlider.querySelectorAll('[data-banner-slide]')];
      const changed = nodes.length !== this.wSlides.length ||
        nodes.some((node, index) => node !== this.wSlides[index]);
      if (!changed && !refreshEditedContent) return;
      // A DOM replacement invalidates the active autoplay cycle as well.
      this.wStop();
      if (changed) {
        const previous = this.wSlides[this.wIndex];
        const previousBlockId = previous?.dataset.blockId;
        const preserved = nodes.findIndex(node =>
          node === previous || (previousBlockId && node.dataset.blockId === previousBlockId));
        this.wSlides = nodes;
        this.wIndex = preserved >= 0 ? preserved : Math.min(this.wIndex, Math.max(0, nodes.length - 1));
        this.wDots = [...this.querySelectorAll('[data-banner-dot]')];
        // Old in-flight media jobs belong to nodes the editor may have removed.
        this.wLatest++;
        this.wLoads.clear();
        this.wForegroundLoaded.clear();
      }
      this.wSlides.forEach(slide => {
        slide.querySelectorAll('[data-banner-element]').forEach((part, index) => {
          part.style.setProperty('--home-waitlist-delay', `${Math.min(index * 85, 340)}ms`);
        });
      });
      // One authoritative pass leaves exactly one active slide and all others hidden.
      if (this.wSlides.length) {
        this.wPaint(this.wIndex);
        if (refreshEditedContent) {
          const slide = this.wSlides[this.wIndex];
          slide.removeAttribute('data-banner-loaded');
          this.wForegroundLoaded.delete(this.wIndex);
        }
        this.wLoad(this.wIndex);
        if (this.wRevealed) this.wLoadForeground(this.wIndex);
      }
      if (!this.wEditorSelected) this.wStart();
    }
    wListener(target, type, fn, options={}) {
      target?.addEventListener(type,fn,{...options,signal:this.wSignal});
    }
    wNormalize(i) { return this.wSlides.length ? (i % this.wSlides.length + this.wSlides.length) % this.wSlides.length : 0; }
    wMedia(slide) {
      const imgs = [...slide.querySelectorAll('[data-home-media] img, .momax-home-banner__picture img')];
      return imgs.length ? [...new Set(imgs)] : [...slide.querySelectorAll('img')].slice(0,1);
    }
    wPromote(img, priority='low') {
      img.decoding='async'; img.loading='eager'; img.fetchPriority=priority;
      const picture = img.closest('picture');
      picture?.querySelectorAll('source[data-srcset]').forEach(s=>{
        s.srcset = s.dataset.srcset; s.removeAttribute('data-srcset'); s.removeAttribute('data-banner-lazy-source');
      });
      if (img.dataset.srcset) { img.srcset=img.dataset.srcset; img.removeAttribute('data-srcset'); }
      if (img.dataset.src) { img.src=img.dataset.src; img.removeAttribute('data-src'); }
      img.removeAttribute('data-banner-lazy-image');
    }
    wLoad(index) {
      if (!this.wSlides.length) return Promise.resolve();
      const i = this.wNormalize(index), slide = this.wSlides[i];
      if (slide.dataset.bannerLoaded === 'true') return Promise.resolve();
      if (this.wLoads.has(i)) return this.wLoads.get(i);
      const media=this.wMedia(slide);
      if (!media.length) { slide.dataset.bannerLoaded='true'; return Promise.resolve(); }
      const job=Promise.all(media.map(img=>new Promise(resolve=>{
        let finished=false, deadline;
        const done=()=>{
          if(finished) return;
          finished=true;
          clearTimeout(deadline);
          img.removeEventListener('load',done);
          img.removeEventListener('error',done);
          resolve();
        };
        img.addEventListener('load',done,{once:true});
        img.addEventListener('error',done,{once:true});
        this.wPromote(img,i===0?'high':'low');
        if(img.complete || (!img.getAttribute('src') && !img.srcset && !img.currentSrc)) done();
        else deadline=setTimeout(done,3500);
      }))).then(()=>{slide.dataset.bannerLoaded='true';}).finally(()=>this.wLoads.delete(i));
      this.wLoads.set(i,job); return job;
    }
    wLoadForeground(index) {
      if (!this.wSlides.length) return;
      const i=this.wNormalize(index);
      if(this.wForegroundLoaded.has(i)) return;
      this.wForegroundLoaded.add(i);
      const slide=this.wSlides[i], media=new Set(this.wMedia(slide));
      slide.querySelectorAll('img').forEach(img=>{ if(!media.has(img)) this.wPromote(img,'low'); });
    }
    wPaint(index) {
      if (!this.wSlides.length) return;
      this.wIndex=this.wNormalize(index);
      this.wElapsed=0;
      this.wVisibleSince=null;
      this.wControls?.classList.remove('is-playing');
      this.wControls?.style.setProperty('--banner-progress','0');
      this.wSlides.forEach((slide,i)=>{
        const active=i===this.wIndex;
        slide.classList.toggle('is-active',active);
        slide.setAttribute('aria-hidden',String(!active));
        slide.inert = !active || !this.wRevealed;
      });
      this.wDots.forEach((dot,i)=>{
        const active=i===this.wIndex;
        dot.classList.toggle('is-active',active);
        dot.setAttribute('aria-current',String(active));
        dot.setAttribute('aria-pressed',String(active));
      });
      this.wUpdatePause();
    }
    async wShow(index) {
      if(!this.wSlides.length) return false;
      const i=this.wNormalize(index),token=++this.wLatest;
      await this.wLoad(i);
      if(!this.isConnected || token!==this.wLatest) return false;
      this.wPaint(i);
      if(this.wRevealed) this.wLoadForeground(i);
      return true;
    }
    async wNavigate(index) {
      this.wStop();
      const changed=await this.wShow(index);
      if(changed) this.wStart();
    }
    wSetRevealed(value) {
      if(this.wRevealed===value) return;
      this.wRevealed=value;
      if (this.owner) this.header(this.wMobile.matches ? 'compact' : (value ? 'compact' : 'full'), false);
      this.classList.toggle('is-revealed',value);
      this.classList.toggle('is-mobile-revealed',value);
      this.wSlides.forEach((slide,i)=>{slide.inert = !value || i!==this.wIndex;});
      if(this.wCue) this.wCue.hidden=false;
      if(value) {
        this.wLoadForeground(this.wIndex);
        this.wStart();
      } else {
        this.wStop();
        this.wPointerCancel(false);
        if(this.wIndex!==0) this.wShow(0);
      }
    }
    wMeasureFirstScreen() {
      if (!this.wStage) return;
      // Shopify can render the header in flow or over the hero. Use the *actual*
      // document start of the sticky stage rather than assuming a fixed header size.
      const stageDocumentTop = window.scrollY + this.getBoundingClientRect().top + (parseFloat(getComputedStyle(this).paddingTop) || 0);
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;
      const isFirstScreen = stageDocumentTop >= 0 && stageDocumentTop < viewportHeight * .40;
      const inset = isFirstScreen ? Math.max(0, Math.round(stageDocumentTop)) : 0;
      this.wFirstScreenInset = inset;
      this.wRevealStart = Math.max(0, stageDocumentTop - inset);
      this.style.setProperty('--home-first-screen-inset', `${inset}px`);
    }
    wRefresh() {
      if (!this.isConnected || !this.wStage || this.wEditorSelected) return;
      const distance=this.offsetHeight-this.wStage.getBoundingClientRect().height;
      if(distance<=0) {this.wSetRevealed(false);return;}
      // First natural wheel/touch scroll reveals the copy, rather than requiring
      // 11% of the entire 130-220vh sticky section to pass.
      const pixels = Math.max(0, window.scrollY - (this.wRevealStart ?? 0));
      if (pixels > 2 && !this.wRevealed) this.wLoadForeground(this.wIndex);
      this.wSetRevealed(this.wRevealed ? pixels > 2 : pixels >= 8);
    }
    wSchedule() {
      if (this.wQueued || !this.isConnected || this.wEditorSelected) return;
      // All-page scroll must not schedule a hero layout read on every frame.
      // The reveal only changes state when crossing its small threshold.
      const pixels = Math.max(0, window.scrollY - (this.wRevealStart ?? 0));
      const shouldReveal = this.wRevealed ? pixels > 2 : pixels >= 8;
      if (shouldReveal === this.wRevealed) return;
      this.wQueued = true;
      requestAnimationFrame(() => {
        this.wQueued = false;
        if (this.isConnected) this.wRefresh();
      });
    }
    wScrollForward() {
      // The button advances just far enough to unlock the hero immediately;
      // no 20%-of-page-length jump or scroll through the extra sticky area.
      const target = Math.max(window.scrollY, (this.wRevealStart ?? 0) + 32);
      window.scrollTo({top:target,behavior:this.wReduce.matches?'auto':'smooth'});
    }
    wClick(e) {
      if(Date.now()<this.wClickSuppress){e.preventDefault();e.stopPropagation();return;}
      if(e.target.closest('[data-home-mobile-scroll-trigger], [data-mm-hero-scroll-cue]')){
        e.preventDefault();this.wScrollForward();
        if(document.activeElement?.blur && this.contains(document.activeElement)) document.activeElement.blur();
        return;
      }
      if(!this.wRevealed) return;
      const dot=e.target.closest('[data-banner-dot]');
      if(dot){e.preventDefault();this.wNavigate(Number(dot.dataset.slideTo)||0);return;}
      const arrow=e.target.closest('[data-banner-direction]');
      if(arrow){e.preventDefault();this.wNavigate(this.wIndex+(Number(arrow.dataset.bannerDirection)||1));return;}
      if(e.target.closest('[data-banner-pause]')){
        e.preventDefault();this.wPaused=!this.wPaused;
        if(this.wPaused)this.wStop();else this.wStart();
        this.wUpdatePause();
      }
    }
    wStart() {
      if(!this.wAutoplay||!this.wRevealed||this.wSlides.length<2||this.wPaused||this.wEditorSelected||this.wFocusPaused||this.wReduce.matches||document.hidden||!this.wInView||this.wTimer!==null||this.wVisibleSince!==null)return;
      const remaining=Math.max(0,this.wInterval-this.wElapsed);
      this.wVisibleSince=performance.now();
      if(this.wControls){
        this.wControls.style.setProperty('--banner-duration',`${this.wInterval}ms`);
        this.wControls.style.setProperty('--banner-progress-delay',`${-this.wElapsed}ms`);
        this.wControls.classList.remove('is-playing');
        void this.wControls.offsetWidth;
        this.wControls.classList.add('is-playing');
      }
      const next=this.wNormalize(this.wIndex+1);
      this.wPreloadTimer=window.setTimeout(()=>{
        this.wPreloadTimer=null;
        if(!this.wRevealed||!this.wInView||document.hidden)return;
        this.wLoad(next);
        this.wLoadForeground(next);
      },Math.max(0,remaining-1200));
      this.wTimer=window.setTimeout(async()=>{
        this.wTimer=null;
        this.wVisibleSince=null;
        if(!this.wRevealed||!this.wInView||document.hidden||this.wPaused||this.wEditorSelected||this.wFocusPaused)return;
        if(await this.wShow(next))this.wStart();
      },remaining);
      this.wUpdatePause();
    }
    wStop() {
      // Prevent a slow autoplay request from changing the slide after the user navigates.
      this.wLatest++;
      if(this.wVisibleSince!==null){
        this.wElapsed=Math.min(this.wInterval,this.wElapsed+performance.now()-this.wVisibleSince);
        this.wVisibleSince=null;
      }
      this.wControls?.style.setProperty('--banner-progress',String(this.wElapsed/this.wInterval));
      this.wControls?.classList.remove('is-playing');
      if(this.wTimer!==null)clearTimeout(this.wTimer);
      if(this.wPreloadTimer!==null)clearTimeout(this.wPreloadTimer);
      this.wTimer=null;this.wPreloadTimer=null;
      this.wUpdatePause();
    }
    wUpdatePause(){
      if(!this.wPause)return;
      const paused=this.wPaused||this.wVisibleSince===null;
      this.wPause.setAttribute('aria-pressed',String(paused));
      this.wPause.setAttribute('aria-label',paused?'Play banner autoplay':'Pause banner autoplay');
    }
    wUnlocked(){return this.wRevealed && this.wSlides.length>1;}
    wPointerDown(e){
      if(!this.wUnlocked()||e.target.closest?.('a,button,input,textarea,select,label,summary,[role="button"]')||(e.pointerType==='mouse' && e.button!==0))return;
      this.wPointer={id:e.pointerId,x:e.clientX,y:e.clientY,horizontal:false};
      this.wStop();
      if(e.pointerType==='mouse')try{this.wStage.setPointerCapture(e.pointerId)}catch(_){}
    }
    wPointerMove(e){
      const p=this.wPointer;
      if(!p||p.id!==e.pointerId)return;
      const dx=e.clientX-p.x,dy=e.clientY-p.y;
      if(!p.horizontal && Math.abs(dy)>12 && Math.abs(dy)>Math.abs(dx)){this.wPointerCancel();return;}
      if(!p.horizontal && Math.abs(dx)>10 && Math.abs(dx)>Math.abs(dy)){p.horizontal=true;this.classList.add('is-swiping');}
      if(p.horizontal && e.cancelable)e.preventDefault();
    }
    wPointerUp(e){
      const p=this.wPointer;if(!p||p.id!==e.pointerId)return;
      const dx=e.clientX-p.x,move=p.horizontal && Math.abs(dx)>=45;
      this.wPointerCancel(false);
      if(move && this.wUnlocked()){this.wClickSuppress=Date.now()+320;this.wNavigate(this.wIndex+(dx<0?1:-1));}
      else this.wStart();
    }
    wPointerCancel(restart=true){
      if(this.wPointer && this.wPointer.id!==undefined)try{this.wStage?.releasePointerCapture(this.wPointer.id)}catch(_){}
      this.wPointer=null;this.classList.remove('is-swiping');
      if(restart)this.wStart();
    }
    wWheel(e){
      if(!this.wUnlocked()||e.ctrlKey)return;
      const dx=Math.abs(e.deltaX)>Math.abs(e.deltaY)?e.deltaX:(e.shiftKey?e.deltaY:0);
      if(Math.abs(dx)<18)return;
      if(e.cancelable)e.preventDefault();
      const now=Date.now();if(now-this.wLastWheel<620)return;
      this.wLastWheel=now;
      this.wNavigate(this.wIndex+(dx>0?1:-1));
    }
    destroyWaitlist() {
      this.wStop();
      this.wObserver?.disconnect();
      this.wEditorObserver?.disconnect();
      this.wAbort?.abort();
      this.wPointerCancel(false);
      if(this.headerShell && this.owner){
        this.headerShell.removeAttribute('data-home-banner-header-slot');
        this.headerShell.style.removeProperty('--home-header-slot-height');
      }
    }
  });
})();
